import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { COMPANY_COLORS } from '@/constants/colors.constant'
import {
  useAttributionDailySummaries,
  useAttributionDateRange,
  useAttributionPeriodLabel,
  useAttributionTotalReturns,
} from '@/contexts/FAAttributionDataContext/hooks'
import { useIsAnalysisLoading } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { formatReturnValue } from '@/utils/attributionReturnFormat'
import { mapDailyReturns } from '@/utils/dailyReturnUtils'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useMemo } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

const POSITIVE_COLOR = COMPANY_COLORS.SUCCESS
const NEGATIVE_COLOR = COMPANY_COLORS.ERROR

// Portfolio Return card: the aggregate value (totalReturn API) on the left with a sparkline of the
// daily portfolio return series (summary API 'p' field) on the right.
const PortfolioReturnChartCard: React.FC<CardProps> = ({ cardId }) => {
  const theme = useTheme()
  const isLoading = useIsAnalysisLoading()
  const totalReturn = useAttributionTotalReturns()
  const dailySummaries = useAttributionDailySummaries()
  const dateRange = useAttributionDateRange()
  const periodLabel = useAttributionPeriodLabel()
  const title = CardTitleLookup[cardId]

  const value = totalReturn == null ? null : totalReturn.portfolioContribution
  const { text: valueText, sign } = formatReturnValue(value, isLoading)
  const valueColor = sign === 'none' ? theme.textSecondary : sign === 'positive' ? POSITIVE_COLOR : NEGATIVE_COLOR

  // Daily portfolio return ('p') series, clamped to the analyzed range.
  const series = useMemo(() => {
    const mapped = mapDailyReturns(dailySummaries ?? undefined) ?? []
    const inRange = dateRange
      ? mapped.filter((p) => {
          const t = new Date(p.date).getTime()
          return t >= dateRange.beginDate.startOf('day').valueOf() && t <= dateRange.endDate.endOf('day').valueOf()
        })
      : mapped
    return inRange.map((p) => p.portfolio)
  }, [dailySummaries, dateRange])
  const hasChart = series.length > 1

  const sparkColor = sign === 'negative' ? NEGATIVE_COLOR : POSITIVE_COLOR
  const chartOptions = useMemo(
    (): Highcharts.Options => ({
      chart: { type: 'areaspline', backgroundColor: 'transparent', margin: [4, 0, 4, 0], animation: false },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: { visible: false },
      yAxis: { visible: false },
      tooltip: { enabled: false },
      plotOptions: {
        areaspline: {
          lineWidth: 2,
          color: sparkColor,
          // Fill from the line down to the bottom (a mountain), not split around the zero line.
          threshold: -Infinity,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, Highcharts.color(sparkColor).setOpacity(0.35).get('rgba') as string],
              [1, Highcharts.color(sparkColor).setOpacity(0).get('rgba') as string],
            ],
          },
          marker: { enabled: false },
          states: { hover: { enabled: false } },
        },
      },
      series: [{ type: 'areaspline', data: series }],
    }),
    [series, sparkColor],
  )

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack sx={{ borderBottom: `1px solid ${theme.border}` }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme.textPrimary,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
      </Stack>
      {periodLabel && (
        <Typography sx={{ fontSize: '12px', color: theme.textSecondary, mt: '4px' }}>
          {periodLabel}
        </Typography>
      )}
      <Stack direction='row' sx={{ flex: 1, minHeight: 0, alignItems: 'center', gap: '12px' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: valueColor, flexShrink: 0 }}>
          {valueText}
        </Typography>
        {hasChart && (
          <Stack sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch' }}>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              containerProps={{ style: { height: '100%', width: '100%' } }}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default PortfolioReturnChartCard
